import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';

// Context
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';

// Utils
import { initializePreloading, preloadNextLikelyPage } from './utils/preloader';

// Hooks
import { usePageTracking } from './hooks/useAnalytics';

// Components
import AnimationEnhancer from './components/shared/AnimationEnhancer';
import ControlsContainer from './components/shared/ControlsContainer';
import ProtectedRoute from './components/shared/ProtectedRoute';

// Lazy-loaded Pages for better performance
const HomePage = React.lazy(() => import('./pages/HomePage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const TeamPage = React.lazy(() => import('./pages/TeamPage'));
const AcademyPage = React.lazy(() => import('./pages/AcademyPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const AssessmentPage = React.lazy(() => import('./pages/AssessmentPage'));
const FreeModule = React.lazy(() => import('./pages/FreeModule'));
const SingleModule = React.lazy(() => import('./pages/SingleModule'));
const AgentsAutomation = React.lazy(() => import('./pages/AgentsAutomation'));
const NextSteps = React.lazy(() => import('./pages/NextSteps'));
const UIBasics = React.lazy(() => import('./pages/UIBasics'));
const WebScraping = React.lazy(() => import('./pages/WebScraping'));
const PaymentPage = React.lazy(() => import('./pages/PaymentPage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const UnlockPage = React.lazy(() => import('./pages/UnlockPage'));
const VibeCodingPage = React.lazy(() => import('./pages/VibeCodingPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const RefundPage = React.lazy(() => import('./pages/RefundPage'));

// Loading component for Suspense fallback
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// App Routes Component with preloading and analytics
const AppRoutes: React.FC = () => {
  const location = useLocation();
  
  // Track page views for analytics
  usePageTracking();
  
  // Preload next likely page based on current route
  useEffect(() => {
    preloadNextLikelyPage(location.pathname);
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/academy" element={<AcademyPage />} />
      <Route path="/vibe-coding" element={<VibeCodingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/chat" element={<ChatPage />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/assessments" 
        element={
          <ProtectedRoute>
            <AssessmentPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/academy/free-module" 
        element={<FreeModule />} 
      />
      <Route 
        path="/academy/single-module" 
        element={<SingleModule />} 
      />
      <Route 
        path="/academy/agents-automation" 
        element={<AgentsAutomation />} 
      />
      <Route 
        path="/academy/next-steps" 
        element={<NextSteps />} 
      />
      <Route 
        path="/academy/ui-basics" 
        element={<UIBasics />} 
      />
      <Route 
        path="/academy/web-scraping" 
        element={<WebScraping />} 
      />
      <Route 
        path="/payment" 
        element={<PaymentPage />} 
      />
      <Route 
        path="/payment/success" 
        element={<PaymentSuccessPage />} 
      />
      <Route 
        path="/unlock" 
        element={<UnlockPage />} 
      />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund" element={<RefundPage />} />
    </Routes>
  );
};

function App() {
  // Initialize preloading strategies
  useEffect(() => {
    initializePreloading();
  }, []);

  return (
    <AuthProvider>
      <LanguageProvider>
        <Router>
          <ScrollToTop />
          <div className="App min-h-screen bg-gray-50">
            <AnimationEnhancer />
            <ControlsContainer />

            <Suspense fallback={<PageLoader />}>
              <AppRoutes />
            </Suspense>
            <SpeedInsights />
        </div>
      </Router>
    </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
